import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '@/firebase/init';
import {
  AddConnector,
  AvailableConnector,
  ConnectorFromStore,
  LocalAdAccount,
} from '@/types/Connectors';
import { getWorkspaceConnectorsPath } from '@/utils/queryPaths';
import { IndexDB } from '@/utils/indexDB';
import { ConnectorFromStoreSchema } from './connectorSchema';

export class ConnectorsService {
  static async getConnectors(workspaceId: string) {
    const connectors: ConnectorFromStore[] = [];
    const snapshot = await getDocs(
      collection(db, getWorkspaceConnectorsPath(workspaceId)),
    );

    snapshot.forEach((connector) => {
      const data = { id: connector.id, ...connector.data() };
      const result = ConnectorFromStoreSchema.safeParse(data);
      if (result.success) {
        connectors.push(result.data as ConnectorFromStore);
      }
    });
    return connectors;
  }

  static async updateName(workspaceId: string, connectorId: string, name: string) {
    await updateDoc(doc(db, getWorkspaceConnectorsPath(workspaceId), connectorId), {
      name,
      last_modified_date: serverTimestamp(),
      last_modified_by: (await IndexDB.getUserEmail()) as string,
    });
  }

  static async getConnector(workspaceId: string, connectorId: string) {
    const snapshot = await getDoc(
      doc(db, getWorkspaceConnectorsPath(workspaceId), connectorId),
    );
    if (!snapshot.exists()) {
      return null;
    }
    return { id: snapshot.id, ...snapshot.data() } as ConnectorFromStore;
  }

  static async updateConnector(
    workspaceId: string,
    connectorId: string,
    data: object,
    userEmail?: string,
  ) {
    let email = userEmail;
    if (!email) {
      email = (await IndexDB.getUserEmail()) as string;
    }
    await updateDoc(doc(db, getWorkspaceConnectorsPath(workspaceId), connectorId), {
      ...data,
      last_modified_by: email,
      last_modified_date: serverTimestamp(),
    });
    return null;
  }

  static async addConnector(
    workspaceId: string,
    connectorId: string,
    data: object,
    userEmail: string,
    organizationId: string,
    oauthId: string | null = null,
    shouldBeDeactivated = false,
  ) {
    await setDoc(doc(db, getWorkspaceConnectorsPath(workspaceId), connectorId), {
      ...data,
      created_date: serverTimestamp(),
      created_by: userEmail,
      last_modified_by: userEmail,
      last_modified_date: serverTimestamp(),
      status: shouldBeDeactivated ? 'DEACTIVATION_SUCCESS' : 'ACTIVATION_SUCCESS',
      organizationId,
      oauthId,
      connectorId,
      isInitialSetup: shouldBeDeactivated,
    });
    return connectorId;
  }

  /**
   * Sets up a connector by either updating an existing one or adding a new one if it doesn't exist.
   *
   * @param workspaceId - The ID of the workspace.
   * @param connectorId - The ID of the connector.
   * @param data - The data for adding or updating the connector.
   * @param userEmail - The email of the user performing the operation.
   * @param organizationId - The ID of the organization.
   * @param oauthId - The OAuth UID.
   */
  static async setupConnector<AccountType = LocalAdAccount>(
    workspaceId: string,
    connectorId: string,
    data: AddConnector<AccountType>,
    userEmail: string,
    organizationId: string,
    oauthId?: string,
  ) {
    const docRef = doc(db, getWorkspaceConnectorsPath(workspaceId), connectorId);
    const possibleConnector = await getDoc(docRef);
    if (possibleConnector.exists()) {
      const updateData = { config: data.config, status: 'DEACTIVATION_SUCCESS' };
      await ConnectorsService.updateConnector(
        workspaceId,
        connectorId,
        updateData,
        userEmail,
      );
    } else {
      await ConnectorsService.addConnector(
        workspaceId,
        connectorId,
        data,
        userEmail,
        organizationId,
        oauthId,
      );
    }
  }

  static async getAvailableConnectors(workspaceId: string) {
    const addedConnectors = await ConnectorsService.getConnectors(workspaceId);
    const allConnectors: AvailableConnector[] = [];
    const snapshot = await getDocs(collection(db, 'available_connectors'));
    snapshot.forEach((connector) => {
      const connectorExists = addedConnectors.find(
        (addedConnector) => addedConnector.id === connector.id,
      );
      allConnectors.push({
        id: connector.id,
        ...connector.data(),
        isAdded: !!connectorExists,
      } as AvailableConnector);
    });
    return allConnectors;
  }

  static async acknowledgeAdAccountError(
    workspaceId: string,
    connectorId: string,
    adAccountId: string,
    status: string,
  ) {
    const connector = await ConnectorsService.getConnector(workspaceId, connectorId);
    if (connector) {
      const shouldRevertToDeletion = status && !status.includes('DELETION');
      const newStatus = shouldRevertToDeletion
        ? `ACCOUNT_DELETION_SUCCESS`
        : `ACCOUNT_SUCCESS`;
      const adAccounts = connector.config.accounts.map((adAccount) => {
        if (adAccount.id === adAccountId) {
          return { ...adAccount, status: newStatus };
        }
        return adAccount;
      });

      await ConnectorsService.updateConnector(workspaceId, connectorId, {
        config: { ...connector.config, accounts: adAccounts },
      });
    }
  }

  static async removeAdAccount(
    workspaceId: string,
    connectorId: string,
    adAccountId: string,
  ) {
    const connector = await ConnectorsService.getConnector(workspaceId, connectorId);

    if (!connector) return null;

    const adAccounts = connector.config.accounts.filter(
      (adAccount) => adAccount.id !== adAccountId,
    );

    await ConnectorsService.updateConnector(workspaceId, connectorId, {
      config: { ...connector.config, accounts: adAccounts },
    });
    return null;
  }
}
